import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { X } from "lucide-react";

import styles from "./team.module.scss";

const API = `${process.env.REACT_APP_API_ROOT}/api/admin`;

const authHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return { Authorization: `Bearer ${user.token}` };
};

/**
 * Create or edit a team member: name, email, password, role, and a
 * feature × action matrix.
 *
 * The matrix starts from the role preset. Ticking a box that differs from the
 * preset turns the account into a "custom" one — the whole matrix is then sent
 * as overrides. "Reset to role defaults" clears the overrides again.
 */
function AdminFormModal({ admin, catalog, onClose, onSaved }) {
  const isNew = !admin;

  const [userName, setUserName] = useState(admin?.userName || "");
  const [email, setEmail] = useState(admin?.email || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(admin?.role || "lister");
  const [permissions, setPermissions] = useState({});
  const [isCustom, setIsCustom] = useState(Boolean(admin?.hasOverrides));
  const [saving, setSaving] = useState(false);

  const presets = useMemo(
    () => Object.fromEntries((catalog.roles || []).map((r) => [r.key, r.permissions])),
    [catalog.roles]
  );

  // Seed the matrix: an existing member shows their effective rights, a new one
  // shows the selected role's preset.
  useEffect(() => {
    if (admin?.permissions && admin.hasOverrides) {
      setPermissions(admin.permissions);
    } else {
      setPermissions(presets[role] || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, presets]);

  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    // Switching role always re-seeds from that role's preset.
    setPermissions(presets[nextRole] || {});
    setIsCustom(false);
  };

  const toggle = (feature, action) => {
    setPermissions((prev) => {
      const current = prev[feature] || [];
      const next = current.includes(action)
        ? current.filter((a) => a !== action)
        : [...current, action];
      return { ...prev, [feature]: next };
    });
    setIsCustom(true);
  };

  const resetToRole = () => {
    setPermissions(presets[role] || {});
    setIsCustom(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!userName.trim()) return toast.error("Name is required");
    if (isNew && !email.trim()) return toast.error("Email is required");
    if (isNew && password.trim().length < 8) {
      return toast.error("Password must be at least 8 characters");
    }
    if (!isNew && password && password.trim().length < 8) {
      return toast.error("Password must be at least 8 characters");
    }

    setSaving(true);
    try {
      // Only send overrides when the matrix actually differs from the preset;
      // an empty object tells the API to fall back to the role.
      const payload = {
        userName: userName.trim(),
        role,
        permissions: isCustom ? permissions : {},
      };
      if (password.trim()) payload.password = password.trim();

      if (isNew) {
        await axios.post(
          `${API}/admins`,
          { ...payload, email: email.trim() },
          { headers: authHeaders() }
        );
        toast.success("Team member added");
      } else {
        await axios.put(`${API}/admins/${admin._id}`, payload, {
          headers: authHeaders(),
        });
        toast.success("Team member updated");
      }
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      ariaHideApp={false}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <form onSubmit={handleSubmit}>
        <div className={styles.modalHeader}>
          <h3>{isNew ? "Add team member" : `Edit ${admin.userName}`}</h3>
          <button type="button" className={styles.iconBtn} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.fieldRow}>
            <label className={styles.field}>
              <span>Name</span>
              <input
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Full name"
              />
            </label>

            <label className={styles.field}>
              <span>Email</span>
              <input
                type="email"
                value={email}
                disabled={!isNew}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@penspace.in"
              />
            </label>
          </div>

          <div className={styles.fieldRow}>
            <label className={styles.field}>
              <span>{isNew ? "Password" : "New password (optional)"}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isNew ? "At least 8 characters" : "Leave blank to keep"}
              />
              {!isNew && (
                <small className={styles.hint}>
                  Changing the password signs them out of every device.
                </small>
              )}
            </label>

            <label className={styles.field}>
              <span>Role</span>
              <select value={role} onChange={(e) => handleRoleChange(e.target.value)}>
                {(catalog.roles || []).map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.matrixHeader}>
            <div>
              <strong>Permissions</strong>
              {isCustom && <span className={styles.customTag}>custom</span>}
            </div>
            {isCustom && (
              <button type="button" className={styles.linkBtn} onClick={resetToRole}>
                Reset to role defaults
              </button>
            )}
          </div>

          <div className={styles.matrixWrap}>
            <table className={styles.matrix}>
              <thead>
                <tr>
                  <th>Section</th>
                  {(catalog.actions || []).map((action) => (
                    <th key={action}>{action}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(catalog.features || []).map((feature) => (
                  <tr key={feature.key}>
                    <td className={styles.featureName}>{feature.label}</td>
                    {(catalog.actions || []).map((action) => {
                      // A feature may not support every action — comments can
                      // only be read or deleted. The cell stays, so the grid
                      // still lines up under its heading, but there is nothing
                      // in it to tick.
                      const grantable = feature.actions
                        ? feature.actions.includes(action)
                        : true;
                      return (
                        <td key={action}>
                          {grantable ? (
                            <input
                              type="checkbox"
                              checked={(permissions[feature.key] || []).includes(action)}
                              onChange={() => toggle(feature.key, action)}
                            />
                          ) : (
                            <span className={styles.notApplicable} aria-hidden="true">
                              —
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Cancel
          </button>
          {/* A plain button, not the shared <Button>: that component hardcodes
              type="button" (so the form never submits) and renders a `text`
              prop rather than children (so it came out blank). */}
          <button type="submit" className={styles.primaryBtn} disabled={saving}>
            {saving ? "Saving…" : isNew ? "Create member" : "Save changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AdminFormModal;
